import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '../../shared/api/errors';
import { useRealtimeReconnect } from '../../shared/api/realtime';
import { Button } from '../../shared/ui/button';
import { PageHeader } from '../../shared/ui/layout';
import {
  EmptyState,
  ErrorState,
  ForbiddenState,
  LoadingState,
  NoMatchState,
} from '../../shared/ui/states';
import { ColumnsPanel } from '../../features/event-columns';
import { FlowRedeliver } from '../../features/flow-redeliver';
import { LiveBar } from '../../features/live-stream';
import { RunFilterBar } from '../../features/event-run-filters';
import { flowKeys, listFlows } from '../../entities/flow';
import {
  EMPTY_RUN_FILTERS,
  MAX_RUN_PATHS,
  RUN_COLUMNS,
  listRuns,
  runFacets,
  runFiltersActive,
  runKeys,
  runQuery,
  useRunsLiveUpdate,
  type IRunColumns,
  type IRunFilters,
  type TCopy,
  type TRun,
  type TRunScan,
} from '../../entities/run';
import { RunsChart } from './ui/runs-chart';
import { RUNS_LAYOUT, RunsTable } from './ui/runs-table';

const PAGE_SIZE = 50;
// One preview request per flow: past this many the page asks for the flows to be picked.
const REDELIVER_MAX_FLOWS = 25;

// Keys move by whole minutes: a key built from `Date.now()` would refetch on every render.
const WINDOW_STEP_MS = 60_000;

const COLUMNS_INFO =
  'Every column here exists on every run. A payload path reads the value at that path in ' +
  "each run's trigger request — the body a sender posted — so it is empty on runs a " +
  'schedule or Run now started, and asking for one reads every body on the page.';

const STOPPED_BY: Record<string, string> = {
  rows: 'the row limit',
  bytes: 'the byte limit',
  time: 'the time limit',
  cancelled: 'leaving the page',
};

// Under a payload search an incomplete answer must not look like a complete one.
const scanText = (scan: TRunScan): string => {
  const read = `${scan.scanned.toLocaleString('en-US')} of ${scan.scannedTotal.toLocaleString('en-US')} request bodies read`;
  if (scan.complete) return `${read} · every one in the window`;
  return scan.stoppedBy
    ? `${read} · stopped by ${STOPPED_BY[scan.stoppedBy] ?? scan.stoppedBy}; older runs were not searched`
    : `${read} · a page was found before the end; Older reads on`;
};

interface IRunsPageProps {
  filters: IRunFilters;
  onFilters: (next: IRunFilters) => void;
  picked: IRunColumns;
  onPicked: (next: IRunColumns) => void;
  onOpen: (run: TRun, copy?: TCopy) => void;
  timeZone?: string;
}

export const RunsPage = ({
  filters,
  onFilters,
  picked,
  onPicked,
  onOpen,
  timeZone,
}: IRunsPageProps) => {
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [reading, setReading] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const reconnect = useRealtimeReconnect();
  const table = useRef<HTMLDivElement>(null);

  const filterKey = JSON.stringify(filters);
  useEffect(() => {
    setNow(Date.now());
    setCursors([undefined]);
    setPage(0);
  }, [filterKey]);

  const windowEnd = Math.ceil(now / WINDOW_STEP_MS) * WINDOW_STEP_MS;
  const query = useMemo(() => runQuery(filters, windowEnd), [filters, windowEnd]);
  const paths = picked.paths;

  const runs = useQuery({
    queryKey: [...runKeys.list(query), paths, cursors[page] ?? null],
    queryFn: () =>
      listRuns({
        ...query,
        limit: PAGE_SIZE,
        cursor: cursors[page],
        ...(paths.length > 0 ? { columns: [...paths] } : {}),
      }),
    placeholderData: keepPreviousData,
  });
  const statuses = useQuery({
    queryKey: runKeys.facets({ ...query, fields: ['status'] }),
    queryFn: () => runFacets({ ...query, fields: ['status'] }),
    placeholderData: keepPreviousData,
  });

  // The failed copies in the window can be sent again: one server job per flow, so no flow
  // picked means every published one.
  const allFlows = useQuery({
    queryKey: flowKeys.list({ sort: 'problems', limit: 100 }),
    queryFn: () => listFlows({ sort: 'problems', limit: 100 }),
  });
  const flowNames = useMemo(
    () => new Map((allFlows.data?.data ?? []).map((flow) => [flow.id, flow.name])),
    [allFlows.data],
  );
  const published = (allFlows.data?.data ?? []).filter((flow) => flow.liveVersion);
  const scopeFlows = filters.flowId.length > 0 ? filters.flowId : published.map((flow) => flow.id);
  const redeliverBlocked =
    filters.nodeKey.length > 1 || (filters.destinationId ?? []).length > 1
      ? 'Redeliver works on one step and one destination at a time — pick one of each.'
      : scopeFlows.length > REDELIVER_MAX_FLOWS ||
          (filters.flowId.length === 0 && allFlows.data?.nextCursor)
        ? `Redeliver runs over at most ${REDELIVER_MAX_FLOWS} flows at once — pick the flows.`
        : null;
  const scopeKey = scopeFlows.join(',');
  const redeliverScopes = useMemo(
    () =>
      redeliverBlocked === null && scopeKey && query.from && query.to
        ? scopeKey.split(',').map((flowId) => ({
            flowId,
            nodeKey: filters.nodeKey[0],
            destinationId: filters.destinationId?.[0],
            from: query.from as string,
            to: query.to as string,
          }))
        : [],
    [redeliverBlocked, scopeKey, query.from, query.to, filters.nodeKey, filters.destinationId],
  );
  const redeliverWhere = [
    filters.nodeKey.length > 0 ? 'at the chosen step' : 'at any step',
    (filters.destinationId ?? []).length > 0 ? 'to the chosen destination' : null,
  ]
    .filter(Boolean)
    .join(' ');

  const rows = runs.data?.data ?? [];
  const visibleRunIds = useMemo(() => new Set(rows.map((run) => run.id)), [rows]);

  const live = useRunsLiveUpdate({
    flowIds: filters.flowId,
    visibleRunIds,
    autoFlush: page === 0 && !reading && !filters.q && !filters.search,
    onFlush: () => setNow(Date.now()),
  });

  // ↓ from nowhere in particular lands on the first run; inside the table the rows walk.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (document.activeElement && document.activeElement !== document.body) return;
      const first = table.current?.querySelector<HTMLElement>('[role="row"][tabindex]');
      if (!first) return;
      event.preventDefault();
      first.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const toggle = (runId: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(runId)) next.delete(runId);
      else next.add(runId);
      return next;
    });

  const span = { from: query.from ?? '', to: query.to ?? '' };
  const narrowed = runFiltersActive(filters);
  const forbidden = runs.error instanceof ApiError && runs.error.status === 403;
  const scan = runs.data?.scan ?? null;

  const body = () => {
    // A failed first read shows its error while the retries run, not a skeleton that says nothing.
    const failed = runs.error ?? (runs.isPending ? runs.failureReason : null);
    if (forbidden) return <ForbiddenState needs="events:read" />;
    if (failed) {
      return (
        <div className="py-6">
          <ErrorState error={failed} onRetry={() => void runs.refetch()} />
        </div>
      );
    }
    if (runs.isPending) return <LoadingState rows={10} className="py-6" />;
    if (rows.length === 0 && page === 0) {
      if (narrowed) {
        return (
          <div className="py-6">
            <NoMatchState
              onReset={() => onFilters({ ...EMPTY_RUN_FILTERS, minutes: filters.minutes })}
            />
          </div>
        );
      }
      return (
        <div className="py-6">
          <EmptyState
            title="No runs in this window"
            description="A run starts when a request reaches a flow, a schedule ticks, or someone presses Run now."
            action={
              filters.minutes < 30 * 24 * 60 || filters.from ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    onFilters({
                      ...filters,
                      minutes: 30 * 24 * 60,
                      from: undefined,
                      to: undefined,
                    })
                  }
                >
                  Widen to 30 days
                </Button>
              ) : undefined
            }
          />
        </div>
      );
    }
    return (
      <div
        ref={table}
        className="@container overflow-x-auto"
        onMouseEnter={() => setReading(true)}
        onMouseLeave={() => setReading(false)}
        onFocusCapture={() => setReading(true)}
        onBlurCapture={() => setReading(false)}
      >
        <RunsTable
          runs={rows}
          columns={picked.columns}
          paths={paths}
          expanded={expanded}
          onToggle={toggle}
          onOpen={onOpen}
          now={now}
          span={span}
          timeZone={timeZone}
        />
      </div>
    );
  };

  return (
    <>
      <PageHeader
        width="full"
        title="Events"
        description="Every run, and every copy it made."
        actions={
          <>
            {redeliverBlocked !== null && filters.show === 'problems' && (
              <span className="max-w-form-narrow text-meta text-muted">{redeliverBlocked}</span>
            )}
            {redeliverScopes.length > 0 && (
              <FlowRedeliver
                scopes={redeliverScopes}
                flowName={(flowId) => flowNames.get(flowId) ?? flowId}
                where={redeliverWhere}
              />
            )}
            <ColumnsPanel
              columns={picked.columns}
              options={RUN_COLUMNS}
              info={COLUMNS_INFO}
              layoutId={RUNS_LAYOUT}
              onChange={(columns) => onPicked({ ...picked, columns })}
              payload={{
                paths,
                max: MAX_RUN_PATHS,
                onChange: (next) => onPicked({ ...picked, paths: next }),
              }}
            />
          </>
        }
      />

      <RunFilterBar
        filters={filters}
        onChange={onFilters}
        query={query}
        statusCounts={statuses.data?.status}
      />

      {/* A refused search fails both reads with the same words; the list's card says it once. */}
      {!forbidden && !runs.isError && (
        <RunsChart filters={filters} onFilters={onFilters} timeZone={timeZone} />
      )}

      <LiveBar
        status={live.status}
        pending={live.pending}
        onLoad={live.flush}
        onReconnect={reconnect}
      />

      <div className="px-page">{body()}</div>

      {(rows.length > 0 || page > 0) && !runs.isError && (
        <div className="flex items-center gap-inset px-page py-4">
          {(page > 0 || Boolean(runs.data?.nextCursor)) && (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((current) => current - 1)}
              >
                Newer
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!runs.data?.nextCursor}
                onClick={() => {
                  const next = runs.data?.nextCursor;
                  if (!next) return;
                  setCursors((current) => [...current.slice(0, page + 1), next]);
                  setPage((current) => current + 1);
                }}
              >
                Older
              </Button>
            </>
          )}
          <span className="ml-2 font-mono text-micro text-faint">
            {scan && filters.search
              ? scanText(scan)
              : 'One row per run; open it for its copies. A run shows its worst copy that was not recovered; recovered copies are counted next to it. ↑ ↓ walk the rows, Enter opens one.'}
          </span>
        </div>
      )}
    </>
  );
};
